import { track_api_latency } from '~/src/dash/analytics/analytics';
import { getToken } from '~/src/dash/api/auth/auth';
import { EPadderType } from '~/src/dash/views/networks/network-settings-view-constants';

const REGEX_PARAM = /\/:([a-z][^/]+)/gi;
const REGEX_BODY_ALLOWED = /post|patch|put|delete/i;
const REGEX_FORM_URLENCODED = /application\/x-www-form-urlencoded/i;

type FetchMethods = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export function encodeForm(body: { [key: string]: any }, type: string) {
  if (Object.keys(body).length === 0) {
    return '';
  } else if (REGEX_FORM_URLENCODED.test(type)) {
    return Object.keys(body)
      .filter(function (key) {
        return !!key;
      })
      .map((key) => {
        let value = body[key];
        if (typeof value === 'object') {
          value = JSON.stringify(value);
        }
        return [key, value].map(encodeURIComponent).join('=');
      })
      .join('&');
  }
  return JSON.stringify(body);
}

function parseQuery(
  params?: Array<{ [key: string]: any } | string | string[]> | { [key: string]: any } | string,
): { [key: string]: any } {
  if (Array.isArray(params)) {
    let query = {} as { [key: string]: string };

    params.forEach(function (param) {
      if (typeof param === 'object') {
        query = Object.assign(query, param);
      } else if (Array.isArray(param)) {
        const key = param[0] as string;
        const value = param[1] as any;

        if (key) {
          query[decodeURIComponent(key)] = decodeURIComponent(value || '');
        }
      } else if (typeof param === 'string') {
        const key = param.split('=')[0] as string;
        const value = param.split('=')[1] as any;

        if (key) {
          query[decodeURIComponent(key)] = decodeURIComponent(value || '');
        }
      }
    });
    return query;
  }
  if (typeof params === 'object') {
    return Object.assign({}, params);
  }
  const query = {} as { [key: string]: string };
  (params || '').split('&').forEach((param: string) => {
    const [name, value] = param.split('=');
    if (name) {
      query[decodeURIComponent(name)] = decodeURIComponent(value);
    }
  });
  return query;
}

export function encodeQuery(query: { [key: string]: string }): string {
  return Object.keys(query)
    .filter(function (key) {
      return !!key;
    })
    .map((key) => {
      let value = query[key];
      if (typeof value === 'object') {
        value = JSON.stringify(value);
      }
      return [key, value].map(encodeURIComponent).join('=');
    })
    .join('&');
}

function buildURL(
  pURL: string,
  params: { [key: string]: any },
  body: { [key: string]: any },
): [string, { [key: string]: any } | null] {
  let url = `${pURL}`;
  const query = Object.assign({}, params);
  const form = body ? Object.assign({}, body) : null;

  url = url.replace(REGEX_PARAM, (_, match) => {
    let val;
    if (query && match in query) {
      val = query[match];
      delete query[match];
      return val;
    }
    if (form && match in form) {
      val = form[match];
      delete form[match];
    }
    return val ? '/' + val : '';
  });

  const encQuery = query ? '?' + encodeQuery(query) : undefined;
  if (encQuery) {
    return [url + encQuery, form];
  }
  return [url, form];
}

function noop(value: any) {
  return value;
}

const transforms = {
  response(
    transform?:
      | ((err: boolean, resp: any) => any[])
      | ((err: boolean, resp: any) => any)
      | {
          success: (err: boolean, resp: any) => any;
          error: (err: boolean, resp: any) => any;
        },
  ): {
    success: (resp: any) => any;
    error: (resp: any) => any;
  } {
    if (Array.isArray(transform)) {
      return {
        error(obj: any) {
          let objCopy = Object.assign({}, obj);
          transform.forEach((transformFunc) => {
            objCopy = transformFunc(true, objCopy);
          });
          return objCopy;
        },
        success(obj: any) {
          let objCopy = Object.assign({}, obj);
          transform.forEach((transformFunc) => {
            objCopy = transformFunc(false, objCopy);
          });
          return objCopy;
        },
      };
    }
    if (typeof transform === 'function') {
      return {
        error: transform.bind(null, true),
        success: transform.bind(null, false),
      };
    }
    if (typeof transform === 'object') {
      return {
        error: transform.error.bind(null, true),
        success: transform.success.bind(null, false),
      };
    }
    return {
      error: noop,
      success: noop,
    };
  },
  body(transform: any) {
    if (Array.isArray(transform)) {
      return function (obj: any) {
        let objCopy = Object.assign({}, obj);
        transform.forEach((transformFunc) => {
          objCopy = transformFunc(objCopy);
        });
        return objCopy;
      };
    }
    return transform || noop;
  },
  query(transform: any) {
    if (Array.isArray(transform)) {
      return function (obj: any) {
        let objCopy = Object.assign({}, obj);
        transform.forEach((transformFunc) => {
          objCopy = transformFunc(objCopy);
        });
        return objCopy;
      };
    }
    return transform || noop;
  },
  retryFunc(
    transform?: (cb: (ok: boolean) => void) => void,
  ): (cb: (ok: boolean, timedout: boolean) => void, timeout: number) => void {
    if (typeof transform === 'function') {
      return function (cb: (ok: boolean, timedout: boolean) => void, timeout: number) {
        let exec = false;
        setTimeout(function () {
          if (!exec) {
            exec = true;
            cb(false, true);
          }
        }, timeout);
        transform(function (ok: boolean) {
          if (!exec) {
            exec = true;
            cb(ok, false);
          }
        });
      };
    }
    return function (cb: (ok: boolean, retry: boolean) => void) {
      cb(false, false);
    };
  },
};

function getHeader(headers: { [key: string]: string }, name: string) {
  return headers[name] || headers[name.toLowerCase()];
}

let REQUESTS = [] as XMLHttpRequest[];

function addXHR(xhr: XMLHttpRequest) {
  REQUESTS.push(xhr);
}

function removeXHR(xhr: XMLHttpRequest) {
  REQUESTS = REQUESTS.filter((req) => {
    return xhr !== req;
  });
}

interface IFetchConfig {
  method: FetchMethods;
  timeout?: number;
  isAbortable?: boolean;
  headers: {
    [key: string]: string;
  };
  responseType?: 'text' | 'json';
  body?: string | { [key: string]: string };
}

interface IFetchMethod {
  method: FetchMethods;
  timeout?: number;
  isAbortable?: boolean;
  headers: {
    [key: string]: string;
  };
  responseType?: 'text' | 'json';
  body?: string;
}

function fetch(url: string, cfg: IFetchMethod) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(cfg.method, url, true);
    xhr.timeout = 'timeout' in cfg ? (cfg.timeout as number) : 10000;
    (xhr as any).isAbortable = 'isAbortable' in cfg ? cfg.isAbortable : true;
    Object.keys(cfg.headers).forEach((key) => {
      xhr.setRequestHeader(key, cfg.headers[key]);
    });
    xhr.onload = function () {
      removeXHR(xhr);
      if (cfg.responseType === 'text') {
        if (xhr.status >= 200 && xhr.status < 400) {
          resolve({
            status: xhr.status,
            response: {
              responseText: xhr.responseText,
            },
          });
        } else {
          reject('Failed to get response');
        }
      } else {
        try {
          resolve({
            status: xhr.status,
            response: JSON.parse(xhr.responseText),
          });
        } catch (e) {
          reject('Failed to parse JSON response');
        }
      }
    };
    xhr.onerror = function () {
      removeXHR(xhr);
      reject('Error processing request');
    };
    xhr.ontimeout = function () {
      removeXHR(xhr);
      reject(`Request timed out after ${xhr.timeout}ms`);
    };
    if (REGEX_BODY_ALLOWED.test(cfg.method)) {
      xhr.send(cfg.body as string);
    } else {
      xhr.send();
    }
    addXHR(xhr);
  });
}

function parseRetryTimeout(timeout: any, defaultTimeout: number): number {
  if (isNaN(timeout)) {
    return defaultTimeout;
  }
  return timeout > 0 ? timeout : defaultTimeout;
}

function parseRetryCount(retryCount: any): number {
  const ct = isNaN(retryCount) ? 0 : retryCount;
  return ct < 0 ? 0 : ct;
}

function parseResourceConfig(method: IResourceConfig): [IResourceMethod, IFetchConfig] {
  const METHOD_CFG = {} as IResourceMethod;
  METHOD_CFG.transformResponse = transforms.response(method.transformResponse);
  METHOD_CFG.transformBody = transforms.body(method.transformBody);
  METHOD_CFG.transformQuery = transforms.query(method.transformQuery);
  METHOD_CFG.retryCount = parseRetryCount(method.retryCount);
  METHOD_CFG.retryTimeout = parseRetryTimeout(method.retryTimeout, 60000);
  METHOD_CFG.retryFunc = transforms.retryFunc(method.retryFunc);
  return [
    METHOD_CFG,
    {
      method: method.method,
      timeout: method.timeout,
      isAbortable: method.isAbortable,
      headers: method.headers || {},
      responseType: method.responseType,
      body: method.body,
    },
  ];
}

export interface IResourceConfig {
  method: FetchMethods;
  headers?: {
    [key: string]: string;
  };
  body?:
    | {
        [key: string]: any;
      }
    | string;
  timeout?: number;
  isAbortable?: boolean;
  responseType?: 'text' | 'json';
  retryCount?: number;
  retryTimeout?: number;
  retryFunc?: (cb: (ok: boolean) => void) => void;
  transformBody?: ((body: any) => any) | Array<(body: any) => any>;
  transformQuery?: ((query: any) => any) | Array<(query: any) => any>;
  transformResponse?:
    | ((err: boolean, resp: any) => any[])
    | ((err: boolean, resp: any) => any)
    | {
        success: (err: boolean, resp: any) => any;
        error: (err: boolean, resp: any) => any;
      };
}

interface IResourceMethod {
  retryCount: number;
  retryTimeout: number;
  retryFunc: (cb: (ok: boolean, timedout: boolean) => void, timeout: number) => void;
  transformBody: (body: any) => any;
  transformQuery: (query: any) => any;
  transformResponse: {
    success: (resp: any) => any;
    error: (resp: any) => any;
  };
}

export interface IResourceData {
  query?: Array<{ [key: string]: any } | string | string[]> | { [key: string]: any } | string;
  body?: { [key: string]: any };
}

export function resourceFactory(baseURL: string) {
  return function <TResolve>(resCfg: IResourceConfig) {
    const [METHOD_CFG, FETCH_CFG] = parseResourceConfig(resCfg);
    return function (reqData?: IResourceData): Promise<TResolve> {
      function execRequest(
        resolve: (arg: TResolve) => void,
        reject: (arg: Error) => void,
        retryCount: number,
      ) {
        let start = Date.now();

        const query = METHOD_CFG.transformQuery(parseQuery(reqData ? reqData.query : {}));
        const data = Object.assign({}, reqData, { query: undefined });
        const [url, body] = buildURL(
          baseURL,
          query,
          METHOD_CFG.transformBody(Object.assign({}, FETCH_CFG.body, data.body)),
        );

        fetch(
          url,
          Object.assign(
            {},
            FETCH_CFG,
            data,
            FETCH_CFG.method === 'GET'
              ? {}
              : {
                  body: encodeForm(body || {}, getHeader(FETCH_CFG.headers, 'Content-Type')),
                },
          ),
        ).then(
          (resp: any) => {
            if (resp.status >= 200 && resp.status < 400) {
              const duration_api_call = Date.now() - start;
              start = Date.now();
              const parsed_resp_object = METHOD_CFG.transformResponse.success(resp.response);
              const duration_parse_response = Date.now() - start;

              // only track api response timings if request successful
              try {
                track_api_latency(
                  url,
                  duration_api_call,
                  duration_parse_response,
                  'resource_factory',
                );
              } catch (e) {
                console.warn('error while sending segment.io data: ', e);
              }

              resolve(parsed_resp_object);
            } else if (retryCount < METHOD_CFG.retryCount) {
              METHOD_CFG.retryFunc(function (ok: boolean, timedout: boolean) {
                if (timedout) {
                  reject(new Error('retry function reached timeout'));
                } else if (ok) {
                  // try again
                  execRequest(resolve, reject, retryCount + 1);
                } else {
                  // reject with last response if retry failed
                  reject(METHOD_CFG.transformResponse.error(resp.response));
                }
              }, METHOD_CFG.retryTimeout);
            } else {
              reject(METHOD_CFG.transformResponse.error(resp.response));
            }
          },
          (msg: string) => {
            reject(new Error(msg));
          },
        );
      }
      return new Promise(function (resolve, reject) {
        execRequest(resolve, reject, 0);
      });
    };
  };
}

export interface IUploadConfig {
  publisher_id?: string;
  site_id?: string;
  network_id?: number;
  global_mediafile?: string;
  is_banner?: string;
  creative_id?: string;
  resolutions?: string;
  template_id?: string;
  order_id?: string;
  collection_id?: number;
  padder_type?: EPadderType;
  name: string;
  file: any;
  managerFunc?: (xhr: XMLHttpRequest) => void;
}

export function uploadResourceFactory(basePath: string) {
  return (path: string) => {
    return (uploadCfg: IUploadConfig): Promise<{ status: number; response: any }> => {
      return new Promise((resolve, reject) => {
        const query: { [key: string]: string } = {
          access_token: getToken().access_token,
          name: uploadCfg.name,
        };
        if ('publisher_id' in uploadCfg) {
          query.publisher_id = uploadCfg.publisher_id || '';
        }
        if ('site_id' in uploadCfg) {
          query.site_id = uploadCfg.site_id || '';
        }
        if ('network_id' in uploadCfg) {
          query.network_id = uploadCfg.network_id + '';
        }
        if ('global_mediafile' in uploadCfg) {
          query.global_mediafile = uploadCfg.global_mediafile || '';
        }
        if ('resolutions' in uploadCfg) {
          query.resolutions = uploadCfg.resolutions || '';
        }
        if ('creative_id' in uploadCfg) {
          query.creative_id = uploadCfg.creative_id || '';
        }
        if ('template_id' in uploadCfg) {
          query.template_id = uploadCfg.template_id || '';
        }
        if ('order_id' in uploadCfg) {
          query.order_id = uploadCfg.order_id || '';
        }
        if ('collection_id' in uploadCfg) {
          query.collection_id = uploadCfg.collection_id + '';
        }
        if ('padder_type' in uploadCfg) {
          query.padder_type = uploadCfg.padder_type + '';
        }
        const fd = new FormData();
        fd.append('media', uploadCfg.file);
        // this is superior to query params, in the backend use r.FormValue to parse the data from body
        // instead of pulling from r.Form
        fd.append('upload', JSON.stringify(query));

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${basePath}/${path}?${encodeQuery(query)}`, true);

        if (uploadCfg.managerFunc !== undefined) {
          uploadCfg.managerFunc(xhr);
        }

        xhr.onload = function () {
          try {
            resolve({
              status: xhr.status,
              response: JSON.parse(xhr.responseText),
            });
          } catch (e) {
            reject('Failed to parse JSON response');
          }
        };
        xhr.onerror = function () {
          reject('Error processing request');
        };
        xhr.ontimeout = function () {
          reject(`Request timed out after ${xhr.timeout}ms`);
        };

        xhr.send(fd);
      });
    };
  };
}
